<template>
  <div class="select-teacher-page">
    <!-- Modern Header -->
    <div class="page-header">
      <div class="header-content">
        <h1>👨‍🏫👩‍🏫 Öğretmen Seçimi</h1>
        <p class="header-subtitle">{{ lessonName }} dersi için öğretmen seçin</p>

      </div>
      <div class="header-actions">
        <button @click="goBack" class="btn-back">
          ← Geri Dön
        </button>
      </div>
    </div>

    <main class="page-content">
      <!-- Loading State -->
      <div v-if="isLoading" class="loading-state">
        <div class="loading-spinner"></div>
        <p>Öğretmen yükleniyor...</p>
      </div>
      
      <!-- Error State -->
      <div v-else-if="error" class="error-state">
        <div class="error-icon">👨‍🏫👩‍🏫</div>
        <h3>Öğretmen Yok</h3>
        <p>{{ lessonName }} dersi için öğretmen bulunamadı.</p>
        <button @click="goBack" class="btn-back">
          ← Geri Dön
        </button>
      </div>
      
      <!-- Empty State -->
      <div v-else-if="teachers.length === 0" class="empty-state">
        <div class="empty-icon">👨‍🏫👩‍🏫</div>
        <h3>Bu Ders İçin öğretmen Bulunmamaktadır</h3>
        <p>{{ lessonName }} dersi için henüz Öğretmen atanmamış. Lütfen daha sonra tekrar deneyin.</p>
      </div>
      
      <!-- Teachers Section -->
      <div v-else class="teachers-section">
        <div class="section-header">
          <h2>📋 Mevcut Öğretmenler</h2>
          <div class="section-stats">
            <span class="stat-badge">{{ teachers.length }} Öğretmen</span>
          </div>
        </div>
        
        <div class="teachers-grid">
          <div 
            v-for="teacher in teachers" 
            :key="teacher.id"
            :class="['teacher-card', { 'blocked': teacher.is_blocked }]"
          >
            <div class="teacher-header">
              <div class="teacher-avatar">
                {{ getInitials(teacher.ad, teacher.soyad) }}
              </div>
              <div class="teacher-status">
                <span class="status-badge">Mevcut</span>
              </div>
            </div>
            
            <div class="teacher-content">
              <h3 class="teacher-name">{{ getTeacherFullName(teacher) }}</h3>
              <div class="teacher-lessons">
                <span class="lesson-label">Dersler:</span>
                <span class="lesson-names">{{ getTeacherLessonNames(teacher.lessons) }}</span>
              </div>
            </div>
            
            <div class="teacher-actions">
              <button 
                @click="showAvailabilityDetails(teacher)"
                class="btn-view"
                v-if="teacher.id"
                :disabled="teacher.is_blocked"
                title="Müsaitlikleri İncele"
              >
                👁️ İncele
              </button>
              
              <button 
                class="btn-select" 
                @click="selectTeacher(teacher)"
                :disabled="teacher.is_blocked"
                :title="teacher.is_blocked ? 'Bu öğretmen bloklanmış' : 'Öğretmen Seç'"
              >
                {{ teacher.is_blocked ? '🚫 Bloklu' : '✅ Seç' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Modern Müsaitlik Detay Modal -->
    <div v-if="showAvailabilityModal" class="modal-overlay" @click="closeAvailabilityModal">
      <div class="modal-content availability-modal" @click.stop>
        <div class="modal-header">
          <h3>{{ selectedTeacher?.ad }} {{ selectedTeacher?.soyad }} - Müsaitlik Bilgileri</h3>
          <button @click="closeAvailabilityModal" class="modal-close">&times;</button>
        </div>
        <div class="modal-body">
          <TeacherAvailabilityDisplay 
            :teacherId="selectedTeacher?.id"
            :viewMode="'view'"
            class="modal-view"
            v-if="selectedTeacher?.id"
          />
        </div>
        <div class="modal-actions">
          <button type="button" @click="closeAvailabilityModal" class="btn-secondary">
            ✖️ Kapat
          </button>
        </div>
      </div>
    </div>

    <AppErrorModal :message="errorPopup" @close="closeErrorPopup" />
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import TeacherAvailabilityDisplay from '@/components/TeacherAvailabilityDisplay.vue'
import AppErrorModal from '@/components/AppErrorModal.vue'
import axios from 'axios'

export default {
  name: 'SelectTeacherView',
  components: {
    TeacherAvailabilityDisplay,
    AppErrorModal
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const store = useStore()
    
    const lessonId = route.params.lessonId
    const lessonName = route.query.lessonName || 'Bilinmeyen Ders'
    
    const teachers = ref([])
    const isLoading = ref(false)
    const error = ref('')
    const showAvailabilityModal = ref(false)
    const selectedTeacher = ref(null)

    const errorPopup = ref('')
    const pendingRedirect = ref(null)
    const closeErrorPopup = () => {
      errorPopup.value = ''
      if (pendingRedirect.value) {
        const target = pendingRedirect.value
        pendingRedirect.value = null
        router.push(target)
      }
    }
    
    // 🚨 Güvenlik için sistem durumu kontrolü
    const systemStatus = ref(null)
    const systemStatusInterval = ref(null)
    
    onMounted(async () => {
      // 🔄 Store'dan kullanıcı bilgisini yeniden yükle
      const token = localStorage.getItem('token')
      if (token) {
        try {
          await store.dispatch('fetchCurrentUser')
        } catch (error) {
          console.error('Kullanıcı bilgisi yüklenemedi:', error)
        }
      }
      
      if (!lessonId) {
        error.value = 'Ders ID gereklidir'
        return
      }
      
      await loadTeachers()
      
      // 🚨 Güvenlik: Sistem durumunu kontrol et ve sürekli izle
      await checkSystemStatus()
      startSystemStatusMonitoring()
    })
    
    onUnmounted(() => {
      // 🚨 Güvenlik: Interval'ı temizle
      stopSystemStatusMonitoring()
    })
    
    const loadTeachers = async () => {
      isLoading.value = true
      error.value = ''
      
      try {
        // Hocaları users tablosundan çek
        await store.dispatch('fetchTeachers')
        const allTeachers = store.getters.getTeachers
        
        // lessonId'yi number'a çevir
        const numericLessonId = parseInt(lessonId)
        
        // Sadece bu ders için olan ve bloklanmamış hocaları filtrele
        teachers.value = allTeachers.filter(teacher => {
          // teacher.lessons array'inde bu ders ID'si var mı kontrol et
          const hasLesson = teacher.lessons && teacher.lessons.some(lesson => lesson.id === numericLessonId)
          // Hoca bloklanmamış mı kontrol et
          const isNotBlocked = !teacher.is_blocked
          return hasLesson && isNotBlocked
        })
        
        if (teachers.value.length === 0) {
          error.value = 'Bu ders için öğretmen bulunamadı'
        }
      } catch (err) {
        error.value = 'Öğretmenler yüklenirken bir hata oluştu'
        console.error(err)
      } finally {
        isLoading.value = false
      }
    }
    
    const selectTeacher = (teacher) => {
      // Nested route kullanarak saat seçim sayfasına git
      router.push({
        name: 'StudentSelectTime',
        params: { 
          teacherId: teacher.id 
        },
        query: {
          lessonId: lessonId,
          lessonName: lessonName,
          teacherName: getTeacherFullName(teacher)
        }
      })
    }
    
    const getLessonName = (lessonId) => {
      const lessons = store.getters.getLessons
      const lesson = lessons.find(l => l.id === parseInt(lessonId))
      return lesson ? lesson.name : 'Bilinmeyen Ders'
    }

    const getInitials = (ad, soyad) => {
      if (!ad || !soyad) return '??'
      return (ad.charAt(0) + soyad.charAt(0)).toUpperCase()
    }

    const getTeacherLessonNames = (lessons) => {
      if (!lessons || lessons.length === 0) {
        return 'Bilinmeyen Ders';
      }
      return lessons.map(lesson => getLessonName(lesson.id)).join(', ');
    };

    const getTeacherFullName = (teacher) => {
      if (teacher.ad && teacher.soyad) {
        return `${teacher.ad} ${teacher.soyad}`;
      }
      return teacher.username || 'Bilinmeyen Öğretmen';
    };
    
    const goBack = () => {
      // Öğrenci paneli altındaki ders seçim sayfasına geri dön
      router.push('/panel/ogrenci/select-lesson')
    }

    const showAvailabilityDetails = (teacher) => {
      selectedTeacher.value = teacher
      showAvailabilityModal.value = true
    }

    const closeAvailabilityModal = () => {
      showAvailabilityModal.value = false
      selectedTeacher.value = null
    }
    
    // 🚨 Güvenlik: Sistem durumunu kontrol et
    const checkSystemStatus = async () => {
      try {
        const response = await axios.get('https://api.modernkurs.com/api/system/status/user')
        if (response.data.success) {
          systemStatus.value = response.data
          
          // Sistem hazır değilse güvenlik uyarısı ver
          if (!response.data.can_book) {
    
            errorPopup.value = 'Sistem bakım moduna alındı. Ana sayfaya yönlendiriliyorsunuz.'
            pendingRedirect.value = '/panel/ogrenci'
            return false
          }
          
          return true
        } else {
          console.error('❌ Sistem durumu alınamadı:', response.data.message)
          return false
        }
      } catch (error) {
        console.error('❌ Sistem durumu kontrol hatası:', error)
        return false
      }
    }
    
    // 🚨 Güvenlik: Sistem durumunu sürekli izle
    const startSystemStatusMonitoring = () => {
      // Her 10 saniyede bir sistem durumunu kontrol et
      systemStatusInterval.value = setInterval(async () => {
        const isSystemReady = await checkSystemStatus()
        if (!isSystemReady) {
          // Sistem hazır değilse interval'ı durdur
          stopSystemStatusMonitoring()
        }
      }, 10000) // 10 saniye
    }
    
    // 🚨 Güvenlik: Sistem durumu izlemeyi durdur
    const stopSystemStatusMonitoring = () => {
      if (systemStatusInterval.value) {
        clearInterval(systemStatusInterval.value)
        systemStatusInterval.value = null
      }
    }
    
    return {
      lessonId,
      lessonName,
      teachers,
      isLoading,
      error,
      errorPopup,
      closeErrorPopup,
      showAvailabilityModal,
      selectedTeacher,
      selectTeacher,
      getLessonName,
      getTeacherLessonNames,
      getTeacherFullName,
      getInitials,
      goBack,
      showAvailabilityDetails,
      closeAvailabilityModal,
      // 🚨 Güvenlik fonksiyonları
      systemStatus,
      checkSystemStatus,
      startSystemStatusMonitoring,
      stopSystemStatusMonitoring
    }
  }
}
</script>

<style scoped>
/* Modern Select Teacher Page Styles */
.select-teacher-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

/* Page Header */
.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-content h1 {
  margin: 0 0 0.5rem 0;
  font-size: 2.5rem;
  font-weight: 700;
}

.header-subtitle {
  margin: 0 0 1rem 0;
  opacity: 0.9;
  font-size: 1.1rem;
}



.header-actions {
  display: flex;
  align-items: center;
}

.btn-back {
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.2);
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-back:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

/* Page Content */
.page-content {
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

/* Loading State */
.loading-state {
  text-align: center;
  padding: 3rem;
  color: #6c757d;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #4CAF50;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error State */
.error-state {
  text-align: center;
  padding: 3rem;
  color: #6c757d;
  background: white;
  border-radius: 25px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
}

.error-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
  color: #6c757d;
}

.error-state h3 {
  margin: 0 0 1rem 0;
  color: #333;
  font-size: 1.5rem;
}

.error-state p {
  margin: 0 0 1.5rem 0;
  color: #6c757d;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 3rem;
  color: #6c757d;
  background: white;
  border-radius: 25px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.empty-state h3 {
  margin: 0 0 1rem 0;
  color: #333;
  font-size: 1.5rem;
}

.empty-state p {
  margin: 0;
  color: #6c757d;
}

/* Teachers Section */
.teachers-section {
  margin-bottom: 2rem;
}

.section-header {
  margin-bottom: 2rem;
  text-align: center;
}

.section-header h2 {
  margin: 0 0 0.5rem 0;
  font-size: 2rem;
  color: #333;
  font-weight: 700;
}

.section-stats {
  display: flex;
  justify-content: center;
  margin-top: 1rem;
}

.stat-badge {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
}

/* Teachers Grid */
.teachers-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;
}

.teacher-card {
  background: white;
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  transition: all 0.3s;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 250px;
}

.teacher-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
}

.teacher-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
}

.teacher-card.blocked {
  opacity: 0.5;
  background: #f8f9fa;
  border-color: #dee2e6;
  cursor: not-allowed;
}

.teacher-card.blocked:hover {
  transform: none;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
}

.teacher-card.blocked::before {
  background: linear-gradient(135deg, #6c757d 0%, #5a6268 100%);
}

.teacher-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.teacher-avatar {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 600;
}

.teacher-status {
  display: flex;
  gap: 0.5rem;
}

.status-badge {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
}

.teacher-content {
  flex: 1;
  margin-bottom: 1.5rem;
}

.teacher-name {
  margin: 0 0 1rem 0;
  font-size: 1.5rem;
  color: #333;
  font-weight: 600;
}

.teacher-lessons {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.lesson-label {
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
}

.lesson-names {
  color: #6c757d;
  line-height: 1.5;
}

.teacher-actions {
  display: flex;
  gap: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #f1f3f4;
}

.btn-view {
  background: linear-gradient(135deg, #17a2b8 0%, #138496 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 15px rgba(23, 162, 184, 0.3);
  flex: 1;
}

.btn-view:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(23, 162, 184, 0.4);
}

.btn-select {
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 15px rgba(76, 175, 80, 0.3);
  flex: 1;
}

.btn-select:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(76, 175, 80, 0.4);
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: 20px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 1400px;
  max-height: 90%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.availability-modal {
  max-width: 1400px;
  width: 98%;
  max-height: 98vh;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.modal-header h3 {
  margin: 0;
  color: #333;
  font-size: 1.3rem;
  font-weight: 600;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6c757d;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.3s;
}

.modal-close:hover {
  background: #f8f9fa;
  color: #333;
}

.modal-body {
  flex: 1;
  padding: 2rem;
  overflow-y: auto;
}

.modal-actions {
  display: flex;
  justify-content: center;
  padding: 1.5rem 2rem;
  background: #f8f9fa;
  border-top: 1px solid #dee2e6;
}

.btn-secondary {
  background: linear-gradient(135deg, #6c757d 0%, #5a6268 100%);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 15px rgba(108, 117, 125, 0.3);
}

.btn-secondary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(108, 117, 125, 0.4);
}

/* Responsive tasarım */
@media (max-width: 768px) {
  .page-header {
    padding: 1.5rem 1rem;
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
  
  .header-content h1 {
    font-size: 2rem;
  }
  
  .page-content {
    padding: 1rem;
  }
  
  .teachers-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
  
  .teacher-card {
    padding: 1.5rem;
    min-height: 220px;
  }
  
  .teacher-actions {
    flex-direction: column;
  }
  
  .modal-content {
    width: 95%;
    margin: 1rem;
  }
}

@media (max-width: 480px) {
  .page-header {
    padding: 1rem 0.75rem;
    gap: 0.75rem;
  }
  
  .header-content h1 {
    font-size: 1.5rem;
  }
  
  .header-subtitle {
    font-size: 1rem;
  }
  
  .teacher-card {
    padding: 1rem;
    min-height: 200px;
  }
  
  .teacher-avatar {
    width: 50px;
    height: 50px;
    font-size: 1.2rem;
  }
  
  .modal-content {
    width: 98%;
    margin: 0.5rem;
  }
  
  .modal-header {
    padding: 1rem 1.5rem;
  }
  
  .modal-body {
    padding: 1rem;
  }
}
</style> 